// ============================================================================
// TRADE MIX — what kind of builder the book says you are.
// ----------------------------------------------------------------------------
// Categories come from classifyProjectCategories (src/services/history-search.ts),
// the SAME classifier the Run Sheet and Project History chips use. Reusing it is
// the whole point: a second definition of what "balcony" means would let this
// panel and the Run Sheet disagree about the same jobs in front of the operator.
//
// THE ONE THING A READER MUST NOT MISS: a job holding two trades is counted in
// BOTH rows, so the cells deliberately do not sum to the panel population. That
// is stated in the stamp, repeated in caveats, and it is why total.count is the
// job count in scope rather than the sum of the cells.
// ============================================================================

import type {
    AnalysisContext,
    Cell,
    DerivedRow,
    PanelDef,
    PanelReadiness,
    PanelSeries,
} from '../registry';
import { censusPhrase } from '../model';
import { money, moneyCompact } from '../model';
import { categoryLabel } from '../../history-search';

/**
 * Below this many classified jobs a top-six ranking is mostly ties and a single
 * job reorders the bars, so the panel reports 'thin' and says what steadies it.
 */
const THIN_CLASSIFIED = 8;

interface Bucket {
    id: string;
    count: number;
    value: number;
}

/**
 * Rows that carry at least one trade. Kept separate from the tally because
 * readiness asks a different question from the ranking: not "which trade wins"
 * but "is there enough classified work here for a ranking to mean anything".
 */
function classifiedCount(rows: DerivedRow[]): number {
    let n = 0;
    for (const r of rows) if (r.categories.length > 0) n += 1;
    return n;
}

function tally(rows: DerivedRow[]): Bucket[] {
    const byId = new Map<string, Bucket>();
    for (const r of rows) {
        // De-duplicated per row: select() below counts a row once via
        // Array.includes, so a repeated id here would produce a count the
        // proof cannot reproduce.
        for (const id of new Set(r.categories)) {
            const found = byId.get(id);
            if (found) {
                found.count += 1;
                found.value += r.value;
            } else {
                byId.set(id, { id, count: 1, value: r.value });
            }
        }
    }
    // Count, then value, then id: the last key is arbitrary but stable, so the
    // bars do not reshuffle between renders when two trades tie.
    return [...byId.values()].sort(
        (a, b) => b.count - a.count || b.value - a.value || a.id.localeCompare(b.id),
    );
}

function clamp01(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return n < 0 ? 0 : n > 1 ? 1 : n;
}

function sumValue(rows: DerivedRow[]): number {
    return rows.reduce((s, r) => s + (Number.isFinite(r.value) ? r.value : 0), 0);
}

function buildCell(bucket: Bucket, topCount: number): Cell {
    const id = bucket.id;
    const label = categoryLabel(id);
    return {
        key: id,
        label,
        count: bucket.count,
        value: bucket.value,
        weight: topCount > 0 ? clamp01(bucket.count / topCount) : 0,
        // THE PROOF. Pure over the rows handed in, so picked.length === count
        // and sum(picked.value) === value for the context this cell came from.
        select(rows: DerivedRow[]): DerivedRow[] {
            return rows.filter(r => r.categories.includes(id));
        },
        // THE WORKING. Recomputed over `picked` rather than echoing the cell, so
        // a proof that cannot reproduce its own figure shows the disagreement.
        formula(picked: DerivedRow[]): string {
            return `${picked.length} rows classified ${label} × sum(scope[].amount) ex GST = ${money(sumValue(picked))}`;
        },
        // THE DOOR. The query is the category ID, never the label:
        // matchesRunSheetSearch splits on whitespace and requires token-AND, so
        // the label "Concrete repair" would demand a job whose haystack also
        // says "repair" and would return near-zero rows. Every id is a single
        // token (flood-test is hyphenated, not spaced) and namesTerm(c.id, token)
        // resolves it against the same classifier that counted this bar.
        // exact:false because matchesRunSheetSearch also has a plain-substring
        // leg, so the Run Sheet can hold a superset; the drawer's reconcile line
        // states the true destination count at click time.
        drill: { lane: 'all', query: id, sort: 'value', exact: false },
    };
}

const tradeMix: PanelDef = {
    id: 'trade-mix',
    title: 'Trade mix',
    hint: 'top 6 by job count',
    group: 'work',
    defaultSpan: 6,
    minSpan: 6,
    slim: false,
    mark: 'bars',
    orientation: 'row',
    respectsPeriod: true,

    compute(ctx: AnalysisContext): PanelSeries {
        const rows = ctx.rows;
        const ranked = tally(rows);
        // Every ranked category becomes a cell; the shell caps the CHART. See the
        // same note in top-clients: truncating here truncates the export too.
        const top = ranked;
        const topCount = top[0]?.count ?? 0;
        return {
            cells: top.map(b => buildCell(b, topCount)),
            // The population, NOT the sum of the cells. Multi-trade jobs sit in
            // more than one cell, so a cell sum would exceed the book.
            total: { count: rows.length, value: sumValue(rows) },
            emptySeries: 'Nothing classified into a trade yet. Around 1 in 8 jobs match no trade keyword.',
        };
    },

    readiness(s: PanelSeries, ctx: AnalysisContext): PanelReadiness {
        // An empty window is a shell empty state, neither thin nor dormant.
        if (s.total.count === 0) return 'live';
        if (s.cells.length === 0) return 'dormant';
        return classifiedCount(ctx.rows) < THIN_CLASSIFIED ? 'thin' : 'live';
    },

    activation(s: PanelSeries, ctx: AnalysisContext): string | null {
        if (s.total.count === 0) {
            return 'No jobs in this window, so there is no mix to rank. Widen the period or clear the filter and this fills in.';
        }
        if (s.cells.length === 0) {
            return 'No job in this window carries wording that names a trade. Trades are read from the job title, address and scope wording, so this fills in as scoped jobs land.';
        }
        const classified = classifiedCount(ctx.rows);
        if (classified < THIN_CLASSIFIED) {
            return `Only ${classified} of ${s.total.count} jobs in this window classify into a trade, so one job reorders the ranking. It steadies as more scoped jobs land in the period.`;
        }
        return null;
    },

    // THE SIGNATURE. Verbatim from the spec's source-stamp table, including the
    // double-count clause: a reader who assumes the rows are exclusive mis-reads
    // the panel, so the warning travels with the figure to screen and to paper.
    // censusPhrase carries no noun here because the population is simply every
    // job in the window; trimEnd removes the space the shared helper leaves.
    stamp(s: PanelSeries, ctx: AnalysisContext): string {
        const census = censusPhrase(s.total.count, ctx.window.capped, '').trimEnd();
        return `${census} · classifyProjectCategories · a job with two trades counts in both`;
    },

    fields: [
        'title',
        'address',
        'projectType',
        'formData.scopeObjective',
        'scope[].title',
        'scope[].description',
        'scope[].amount',
        'scopeGstInclusive',
    ],

    caveats: [
        'A job holding two trades counts in both, so the counts do not sum to the job total.',
        'Around 1 in 8 jobs match no trade keyword and appear in no row. Measured against the live 123-project corpus on 2026-07-03: 15 of 123 classified into zero categories.',
    ],

    csv(s: PanelSeries) {
        return {
            headers: ['Trade', 'Jobs', 'Value ex GST', 'Share of top trade'],
            rows: s.cells.map(c => ({
                Trade: c.label,
                Jobs: c.count,
                // The money string the screen shows, so the export and the panel
                // cannot disagree cell for cell.
                'Value ex GST': money(c.value),
                'Share of top trade': `${Math.round(clamp01(c.weight) * 100)}%`,
            })),
        };
    },

    /**
     * Static SVG for the printed report. The print document is served with
     * script-src 'none', so every coordinate is resolved here and serialised;
     * there is no measure-then-adjust pass and no canvas. Colour comes from the
     * injected --qm-accent variable and from currentColor, so the report retints
     * with the tenant brand and degrades to ink rather than to a wrong colour.
     */
    printSvg(s: PanelSeries, w: number, h: number): string {
        const width = Math.max(160, Math.round(Number.isFinite(w) ? w : 0));
        const height = Math.max(32, Math.round(Number.isFinite(h) ? h : 0));
        const open = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="Trade mix, top trades by job count"><title>Trade mix</title>`;

        const cells = s.cells;
        if (cells.length === 0) {
            const line = xmlEscape(s.emptySeries || 'Nothing classified into a trade yet.');
            return `${open}<text x="0" y="${Math.round(height / 2)}" font-family="Inter, system-ui, sans-serif" font-size="9" fill="currentColor" opacity="0.7">${line}</text></svg>`;
        }

        const gap = 6;
        const numW = 26;
        const moneyW = 54;
        const labelW = Math.min(160, Math.max(60, Math.round(width * 0.28)));
        const trackX = labelW + gap;
        const trackW = Math.max(12, width - labelW - numW - moneyW - gap * 3);
        const numX = trackX + trackW + gap + numW;
        const rowH = height / cells.length;
        const barH = Math.max(4, Math.min(12, Math.round(rowH - 6)));

        const parts: string[] = [open];
        cells.forEach((c, i) => {
            const mid = rowH * i + rowH / 2;
            const barY = Math.round(mid - barH / 2);
            const textY = Math.round(mid + 3);
            const fillW = Math.max(3, Math.round(trackW * clamp01(c.weight)));
            parts.push(
                `<text x="0" y="${textY}" font-family="Inter, system-ui, sans-serif" font-size="9" font-weight="700" fill="currentColor">${xmlEscape(c.label)}</text>`,
                `<rect x="${trackX}" y="${barY}" width="${trackW}" height="${barH}" rx="3" fill="currentColor" fill-opacity="0.08"/>`,
                `<rect x="${trackX}" y="${barY}" width="${fillW}" height="${barH}" rx="3" fill="var(--qm-accent)"/>`,
                `<text x="${numX}" y="${textY}" text-anchor="end" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="9" font-weight="700" fill="currentColor">${c.count}</text>`,
                `<text x="${width}" y="${textY}" text-anchor="end" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="8.5" fill="currentColor" opacity="0.7">${xmlEscape(moneyCompact(c.value))}</text>`,
            );
        });
        parts.push('</svg>');
        return parts.join('');
    },
};

/**
 * Labels come from a curated in-repo set today, so this is belt and braces: the
 * output is serialised into an HTML document, and an unescaped ampersand from a
 * future category name would break the whole report rather than one label.
 */
function xmlEscape(text: string): string {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export default tradeMix;
